<template>
  <el-form ref="form"
           class="flow-approval-form"
           :model="formData"
           :label-width="labelWidth"
           :label-position="labelPosition"
           size="small">
    <!-- 前置插槽，用于放置其他表单项 -->
    <slot name="prepend"></slot>

    <!-- 处理意见 -->
    <el-form-item :label="commentLabel">
      <div class="comment-wrapper">
        <el-input v-model="formData.comment"
                  type="textarea"
                  :rows="rows"
                  :maxlength="maxlength"
                  :show-word-limit="showWordLimit"
                  :placeholder="commentPlaceholder">
        </el-input>
        <div class="comment-actions">
          <el-upload ref="imageUpload"
                     name="uploadFile"
                     :action="imageUploadUrl"
                     :with-credentials="true"
                     :show-file-list="false"
                     :before-upload="onImageBeforeUpload"
                     :on-success="onImageUploadSuccess"
                     :on-error="onImageUploadError"
                     accept="image/*"
                     multiple>
            <el-tooltip :content="imageUploading ? '上传中...' : '上传图片'" placement="top">
              <el-button size="small"
                         plain
                         round
                         :loading="imageUploading"
                         :disabled="imageUploading"
                         :icon="imageUploading ? 'el-icon-loading' : 'el-icon-picture'">
              </el-button>
            </el-tooltip>
          </el-upload>
          <el-upload ref="fileUpload"
                     name="uploadFile"
                     :action="fileUploadUrl"
                     :with-credentials="true"
                     :show-file-list="false"
                     :before-upload="onFileBeforeUpload"
                     :on-success="onFileUploadSuccess"
                     :on-error="onFileUploadError"
                     multiple>
            <el-tooltip :content="fileUploading ? '上传中...' : '上传附件'" placement="top">
              <el-button size="small"
                         plain
                         round
                         :loading="fileUploading"
                         :disabled="fileUploading"
                         :icon="fileUploading ? 'el-icon-loading' : 'el-icon-paperclip'">
              </el-button>
            </el-tooltip>
          </el-upload>
        </div>
        <!-- 图片和附件列表区域 -->
        <div class="comment-attachments" v-if="formData.images.length > 0 || formData.files.length > 0">
          <!-- 图片列表 -->
          <div class="image-item" v-for="(image, index) in formData.images" :key="'image-' + index">
            <img :src="image.url" :alt="image.name">
            <i class="el-icon-error remove-btn" @click="handleRemoveImage(image)"></i>
          </div>
          <!-- 附件列表 -->
          <div class="file-item" v-for="(file, index) in formData.files" :key="'file-' + index">
            <i class="el-icon-document"></i>
            <span class="file-name" :title="file.name">{{ file.name }}</span>
            <i class="el-icon-error remove-btn" @click="handleRemoveFile(file)"></i>
          </div>
        </div>
      </div>
    </el-form-item>

    <!-- 后置插槽，用于放置其他表单项 -->
    <slot name="append"></slot>
  </el-form>
</template>

<script>
export default {
  name: 'FlowApprovalForm',
  props: {
    // 表单标签宽度
    labelWidth: {
      type: String,
      default: '0'
    },
    // 标签位置
    labelPosition: {
      type: String,
      default: 'top'
    },
    // 处理意见标签
    commentLabel: {
      type: String,
      default: '处理意见'
    },
    // 处理意见占位符
    commentPlaceholder: {
      type: String,
      default: '输入处理意见'
    },
    // 处理意见是否必填
    commentRequired: {
      type: Boolean,
      default: false
    },
    // 文本框行数
    rows: {
      type: Number,
      default: 3
    },
    // 最大字数
    maxlength: {
      type: Number,
      default: 200
    },
    // 是否显示字数统计
    showWordLimit: {
      type: Boolean,
      default: true
    },
    // 图片上传接口地址
    imageUploadUrl: {
      type: String,
      default: '/workspace/manager/flow/upload/approval_image'
    },
    // 文件上传接口地址
    fileUploadUrl: {
      type: String,
      default: '/workspace/manager/flow/upload/approval_file'
    }
  },
  data () {
    return {
      formData: {
        comment: '',
        images: [],
        files: []
      },
      imageUploading: false,
      fileUploading: false
    }
  },
  methods: {
    // 图片上传前
    onImageBeforeUpload () {
      this.imageUploading = true
      return true
    },
    // 图片上传成功
    onImageUploadSuccess (response, file) {
      this.imageUploading = false
      if (response['responseCode'] === 'SUCCESS') {
        const fileInfo = {
          name: file.name,
          url: response.uploadUrl
        }
        this.formData.images.push(fileInfo)
      } else {
        this.$message.warning((response && response['message']) || '上传失败')
      }
    },
    // 文件上传前
    onFileBeforeUpload () {
      this.fileUploading = true
      return true
    },
    // 文件上传成功
    onFileUploadSuccess (response, file) {
      this.fileUploading = false
      if (response['responseCode'] === 'SUCCESS') {
        const fileInfo = {
          name: file.name,
          url: response.uploadUrl
        }
        this.formData.files.push(fileInfo)
      } else {
        this.$message.warning((response && response['message']) || '上传失败')
      }
    },
    // 图片上传失败
    onImageUploadError () {
      this.imageUploading = false
      this.$message.error('图片上传失败，请稍后重试')
    },
    // 文件上传失败
    onFileUploadError () {
      this.fileUploading = false
      this.$message.error('附件上传失败，请稍后重试')
    },
    handleRemoveImage (image) {
      const index = this.formData.images.indexOf(image)
      if (index >= 0) {
        this.formData.images.splice(index, 1)
      }
    },
    handleRemoveFile (file) {
      const index = this.formData.files.indexOf(file)
      if (index >= 0) {
        this.formData.files.splice(index, 1)
      }
    },
    // 重置表单
    reset () {
      this.formData.comment = ''
      this.formData.images = []
      this.formData.files = []
      this.imageUploading = false
      this.fileUploading = false
    },
    // 校验并返回表单数据
    validate () {
      return new Promise((resolve, reject) => {
        if (this.commentRequired && !this.formData.comment) {
          reject(new Error(`请输入${this.commentLabel}`))
          return
        }
        resolve(this.formData)
      })
    }
  }
}
</script>

<style lang="scss">
.flow-approval-form {
  .el-form-item {
    &:last-child {
      margin-bottom: 0;
    }

    .el-form-item__label {
      padding-bottom: 5px;
      line-height: 1.5;
    }
  }

  .comment-wrapper {
    .el-textarea__inner {
      resize: none;
    }

    .comment-actions {
      margin-top: 10px;
      display: flex;
      align-items: center;

      .el-upload {
        line-height: 1;
      }

      .el-button {
        margin-right: 10px;
        padding: 4px 8px;
        font-size: 16px;
        color: #909399;

        &:hover {
          color: #409eff;
        }
      }
    }

    .comment-attachments {
      margin-top: 10px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: flex-start;

      .image-item {
        position: relative;
        width: 46px;
        height: 46px;
        border-radius: 4px;
        border: 1px solid #dcdfe6;
        flex-shrink: 0;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 4px;
        }

        .remove-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          font-size: 16px;
          color: #f56c6c;
          cursor: pointer;
          background: #fff;
          border-radius: 50%;
          z-index: 10;
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);

          &:hover {
            color: #f78989;
          }
        }
      }

      .file-item {
        position: relative;
        display: flex;
        align-items: baseline;
        justify-content: center;
        padding: 6px 24px 6px 8px;
        background: #f5f7fa;
        border-radius: 4px;
        border: 1px solid #e4e7ed;
        max-width: 160px;
        min-width: 80px;
        height: 46px;
        flex-shrink: 0;

        .el-icon-document {
          font-size: 14px;
          color: #909399;
          margin-right: 4px;
          flex-shrink: 0;
        }

        .file-name {
          font-size: 12px;
          color: #606266;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .remove-btn {
          position: absolute;
          top: -8px;
          right: -8px;
          font-size: 16px;
          color: #f56c6c;
          cursor: pointer;
          background: #fff;
          border-radius: 50%;
          z-index: 10;
          box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);

          &:hover {
            color: #f78989;
          }
        }
      }
    }
  }
}
</style>
